import { useEffect, useState } from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";
import type { Metric } from "web-vitals";

import {
  buildPerformanceBudgetViolations,
  calculatePerformanceScoreFromAverages,
  DEFAULT_PERFORMANCE_BUDGET,
} from "./performanceHelpers";

const logger =
  FrontendLoggerFactory.getLogger().withComponent("utils/performance");

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  timestamp: number;
}

/**
 * Collects Web Vitals and exposes them to the admin UI for diagnostics.
 *
 * @remarks
 * This is intended for development/debugging. Avoid emitting high-volume logs from performance measurement;
 * prefer derived summaries and explicit "check budget" actions.
 */
class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private listeners: Array<(metric: PerformanceMetric) => void> = [];

  constructor() {
    this.initializeWebVitals();
  }

  onMetric(callback: (metric: PerformanceMetric) => void) {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  getAverageMetric(name: string): number | null {
    const relevantMetrics = this.metrics.filter((m) => m.name === name);
    if (relevantMetrics.length === 0) return null;

    return (
      relevantMetrics.reduce((sum, m) => sum + m.value, 0) /
      relevantMetrics.length
    );
  }

  getPerformanceScore(): number {
    return calculatePerformanceScoreFromAverages({
      lcp: this.getAverageMetric("LCP"),
      inp: this.getAverageMetric("INP"),
      cls: this.getAverageMetric("CLS"),
    });
  }

  private initializeWebVitals() {
    const handler = this.handleMetric.bind(this);
    onCLS(handler);
    onINP(handler);
    onFCP(handler);
    onLCP(handler);
    onTTFB(handler);
  }

  private handleMetric(metric: Metric) {
    const performanceMetric: PerformanceMetric = {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      timestamp: Date.now(),
    };

    this.metrics.push(performanceMetric);
    this.notifyListeners(performanceMetric);

    if (metric.rating === "poor") {
      logger.warn("Poor Web Vitals metric detected", {
        operation: "web_vitals_poor_metric",
        metricName: metric.name,
        metricValue: metric.value,
        metricRating: metric.rating,
      });
    }
  }

  private notifyListeners(metric: PerformanceMetric) {
    this.listeners.forEach((listener) => listener(metric));
  }
}

let performanceMonitor: PerformanceMonitor | null = null;

const getPerformanceMonitor = (): PerformanceMonitor => {
  performanceMonitor ??= new PerformanceMonitor();
  return performanceMonitor;
};

export const initializePerformanceMonitoring = (): void => {
  void getPerformanceMonitor();
};

/**
 * React hook that subscribes to `performanceMonitor` updates.
 */
export const usePerformanceMonitoring = () => {
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const monitor = getPerformanceMonitor();

  useEffect(() => {
    const unsubscribe = monitor.onMetric((metric) => {
      setMetrics((prev) => [...prev, metric]);
    });

    return unsubscribe;
  }, [monitor]);

  return {
    metrics,
    averageLCP: monitor.getAverageMetric("LCP"),
    averageINP: monitor.getAverageMetric("INP"),
    averageFCP: monitor.getAverageMetric("FCP"),
    averageCLS: monitor.getAverageMetric("CLS"),
    averageTTFB: monitor.getAverageMetric("TTFB"),
    performanceScore: monitor.getPerformanceScore(),
  };
};

/**
 * Helper for measuring a custom performance span using the Performance API.
 *
 * @remarks
 * Call `start()` before the measured work and `end()` after.
 */
export const trackCustomPerformance = (name: string, startMark?: string) => {
  const endMark = `${name}-end`;

  return {
    start: () => {
      performance.mark(`${name}-start`);
    },

    end: () => {
      performance.mark(endMark);

      try {
        performance.measure(name, startMark ?? `${name}-start`, endMark);
        const measure = performance.getEntriesByName(name, "measure")[0];

        logger.debug("Custom performance measure", {
          operation: "perf_measure",
          name,
          durationMs: Number(measure.duration.toFixed(2)),
        });

        return measure.duration;

        logger.warn("No performance measure found", {
          operation: "perf_measure_missing",
          name,
        });
        return null;
      } catch (error) {
        logger.error(
          "Failed to measure performance",
          error instanceof Error ? error : undefined,
          { operation: "perf_measure_failed", name },
        );
        return null;
      }
    },
  };
};

/**
 * Checks average Web Vitals values against a basic "budget" threshold.
 */
export const checkPerformanceBudget = () => {
  const monitor = getPerformanceMonitor();
  const averageMetrics = Object.keys(DEFAULT_PERFORMANCE_BUDGET).reduce(
    (accumulator, metric) => {
      return {
        ...accumulator,
        [metric]: monitor.getAverageMetric(metric),
      };
    },
    {} as Record<string, number | null>,
  );

  const violations = buildPerformanceBudgetViolations(
    DEFAULT_PERFORMANCE_BUDGET,
    averageMetrics,
  );

  return {
    passed: violations.length === 0,
    violations,
    score: monitor.getPerformanceScore(),
  };
};
